import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { CustomMonthPicker } from "./DatePickers";
import { HrAttendance } from "../../types/HrTypes";
import { attendanceMarkedHow } from "../../utils/attendanceDisplay";
import { codeOf } from "../../utils/leavePolicy";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const LEGEND = [
  { label: "Present", className: "bg-[#D8F0E6] text-[#1F6B52]" },
  { label: "Absent", className: "bg-[#F8DADA] text-[#9B3B3B]" },
  { label: "Half day", className: "bg-[#D9E8F5] text-[#3B6B9B]" },
  { label: "Leave", className: "bg-[#F3E6D8] text-[#8A6A48]" },
  { label: "Holiday", className: "bg-[#E8E0F5] text-[#5B4A8A]" },
  { label: "Week off", className: "bg-[#E8E8E4] text-[#6A6A65]" },
  { label: "Future", className: "bg-[#F0F0EC] text-[#C8C8C3]" },
] as const;

function todayIst() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function parseYm(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  return { year: y || new Date().getFullYear(), month: (m || 1) - 1 };
}

function dateToIso(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function statusText(row?: HrAttendance | null) {
  if (!row) return "No record";
  const key = String(row.status || "").toLowerCase();
  const code = codeOf(row.leaveType);
  if (key === "unpaid" || (key === "on_leave" && code === "UL")) return "Unpaid";
  if (key === "half_day") return code ? `Half day (${code})` : "Half day";
  if (key === "holiday") return code === "HOL" ? "Holiday (quota)" : "Holiday";
  if (key === "on_leave") return row.leaveType?.name || code || "Leave";
  if (key === "weekend") return "Week off";
  if (key === "wfh") return "WFH / remote present";
  if (key === "present") return "Present";
  if (key === "absent") return "Absent";
  return key.split("_").join(" ");
}

function cellTone(iso: string, today: string, row?: HrAttendance | null) {
  if (iso > today) {
    return {
      cell: "bg-[#F0F0EC] text-[#C8C8C3] cursor-default",
      tip: "Future date",
    };
  }
  const key = String(row?.status || (iso < today ? "absent" : "")).toLowerCase();
  if (!row && iso === today) {
    return {
      cell: "bg-white text-[#4A4A48] border border-[#E8E8E4]",
      tip: "Today · not marked yet",
    };
  }
  if (key === "present" || key === "wfh") {
    return { cell: "bg-[#D8F0E6] text-[#1F6B52]", tip: statusText(row) };
  }
  if (key === "absent" || key === "unpaid") {
    return { cell: "bg-[#F8DADA] text-[#9B3B3B]", tip: statusText(row) };
  }
  if (key === "half_day") {
    return { cell: "bg-[#D9E8F5] text-[#3B6B9B]", tip: statusText(row) };
  }
  if (key === "on_leave") {
    return { cell: "bg-[#F3E6D8] text-[#8A6A48]", tip: statusText(row) };
  }
  if (key === "holiday") {
    return { cell: "bg-[#E8E0F5] text-[#5B4A8A]", tip: statusText(row) };
  }
  if (key === "weekend") {
    return { cell: "bg-[#E8E8E4] text-[#6A6A65]", tip: statusText(row) };
  }
  return {
    cell: "bg-white text-[#4A4A48] border border-[#E8E8E4]",
    tip: statusText(row),
  };
}

export function AttendanceMonthCalendar({
  yearMonth,
  onYearMonthChange,
  rows,
}: {
  yearMonth: string;
  onYearMonthChange: (ym: string) => void;
  rows: HrAttendance[];
}) {
  const today = todayIst();
  const { year, month } = parseYm(yearMonth);
  const [hover, setHover] = useState<{
    iso: string;
    x: number;
    y: number;
  } | null>(null);

  const byDate = useMemo(() => {
    const map = new Map<string, HrAttendance>();
    for (const row of rows) {
      if (row.attendance_date > today) continue;
      map.set(row.attendance_date, row);
    }
    return map;
  }, [rows, today]);

  const cells = useMemo(() => {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const list: (string | null)[] = [];
    for (let i = 0; i < firstDow; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      list.push(dateToIso(new Date(year, month, d)));
    }
    return list;
  }, [year, month]);

  const shiftMonth = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    onYearMonthChange(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`,
    );
  };

  useEffect(() => {
    setHover(null);
  }, [yearMonth]);

  const hoverRow = hover ? byDate.get(hover.iso) : undefined;
  const hoverTone = hover ? cellTone(hover.iso, today, hoverRow) : null;
  const marked = hoverRow ? attendanceMarkedHow(hoverRow) : null;

  const tooltip =
    hover && typeof document !== "undefined"
      ? createPortal(
          <div
            className="pointer-events-none fixed z-[200] max-w-[220px] rounded-xl border border-[#E8E8E4] bg-white px-3 py-2 text-xs shadow-lg"
            style={{
              left: Math.min(hover.x + 14, window.innerWidth - 236),
              top: Math.min(hover.y + 14, window.innerHeight - 120),
            }}
          >
            <p className="font-semibold text-[#2C2C2A]">
              {hover.iso}
              {hover.iso === today ? " · Today" : ""}
            </p>
            <p className="mt-1 text-[#4A4A48]">
              Status:{" "}
              <span className="font-semibold">
                {hover.iso > today ? "Future" : hoverTone?.tip}
              </span>
            </p>
            {hover.iso <= today && hoverRow?.leaveType?.name ? (
              <p className="mt-0.5 text-[#8A8A85]">Leave: {hoverRow.leaveType.name}</p>
            ) : null}
            {hover.iso <= today && marked && marked.label !== "—" ? (
              <p className="mt-0.5 text-[#8A8A85]">
                Via: {marked.label}
                {marked.by ? ` by ${marked.by}` : ""}
              </p>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E8E8E4] bg-white">
      <div className="flex items-center gap-2 border-b border-[#E8E8E4] bg-[#FDFBF2] p-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E8E4] bg-white text-[#4A4A48]"
          aria-label="Previous month"
        >
          <FaChevronLeft className="text-xs" />
        </button>
        <CustomMonthPicker
          label="Month"
          value={yearMonth}
          max={today.slice(0, 7)}
          onChange={onYearMonthChange}
          className="min-w-0 flex-1"
        />
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          disabled={yearMonth >= today.slice(0, 7)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E8E4] bg-white text-[#4A4A48] disabled:opacity-40"
          aria-label="Next month"
        >
          <FaChevronRight className="text-xs" />
        </button>
      </div>

      <div className="p-3">
        <div className="mb-3 flex flex-wrap gap-2 text-[10px] font-medium uppercase tracking-wide text-[#8A8A85]">
          {LEGEND.map((item) => (
            <span key={item.label} className={`rounded-md px-2 py-1 ${item.className}`}>
              {item.label}
            </span>
          ))}
        </div>

        <div className="mb-2 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="py-1 text-center text-[10px] font-semibold uppercase text-[#8A8A85]"
            >
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`e-${i}`} />;
            const row = byDate.get(iso);
            const tone = cellTone(iso, today, row);
            const isToday = iso === today;
            return (
              <div
                key={iso}
                onMouseEnter={(e) =>
                  setHover({ iso, x: e.clientX, y: e.clientY })
                }
                onMouseMove={(e) =>
                  setHover((cur) =>
                    cur?.iso === iso ? { iso, x: e.clientX, y: e.clientY } : cur,
                  )
                }
                onMouseLeave={() =>
                  setHover((cur) => (cur?.iso === iso ? null : cur))
                }
                className={`relative flex h-11 flex-col items-center justify-center rounded-lg text-sm font-semibold transition ${tone.cell} ${
                  isToday ? "ring-2 ring-[#345952] ring-offset-1" : ""
                }`}
              >
                <span>{Number(iso.slice(8))}</span>
              </div>
            );
          })}
        </div>

        <p className="mt-3 text-[11px] leading-4 text-[#8A8A85]">
          Hover a date for status. Future days stay gray. Past unmarked days show as absent.
        </p>
      </div>
      {tooltip}
    </div>
  );
}

export function monthAttendanceFetchRange(ym: string) {
  const today = todayIst();
  const [y, m] = ym.split("-").map(Number);
  const from = `${ym}-01`;
  if (from > today) return null;
  const last = new Date(y, m, 0).getDate();
  const monthEnd = `${ym}-${String(last).padStart(2, "0")}`;
  return { from, to: monthEnd > today ? today : monthEnd };
}
